import axios from 'axios';

// const API_BASE_URL = 'http://localhost:5000/api';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : 'https://server.wergame.io/api');

const GET_TIMEOUT_MS = 20000;
const MUTATION_TIMEOUT_MS = 60000;
const AUTH_TIMEOUT_MS = 45000;
const TIMEOUT_USER_MESSAGE =
  'The server is taking longer than usual. Please wait a moment and try again.';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export function isTimeoutError(error) {
  if (!error) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;
  return /timeout of \d+ms exceeded/i.test(String(error.message || ''));
}

export function isNetworkError(error) {
  if (!error) return false;
  if (isTimeoutError(error)) return true;
  return !error.response;
}

export function friendlyApiError(error, fallback = 'Something went wrong. Please try again.') {
  if (isTimeoutError(error)) return TIMEOUT_USER_MESSAGE;
  const fromServer = error?.response?.data?.message;
  if (fromServer) return fromServer;
  if (error?.message && !/timeout of \d+ms exceeded/i.test(error.message)) {
    return error.message;
  }
  return fallback;
}

function defaultTimeoutForRequest(config) {
  const url = `${config.baseURL || ''}${config.url || ''}`;
  const method = String(config.method || 'get').toLowerCase();
  if (url.includes('/auth/')) return AUTH_TIMEOUT_MS;
  if (method === 'get' || method === 'head') return GET_TIMEOUT_MS;
  return MUTATION_TIMEOUT_MS;
}

// Add token to requests if available and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // If data is FormData, let axios set Content-Type automatically (with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    if (config.timeout == null || config.timeout === 0) {
      config.timeout = defaultTimeoutForRequest(config);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 403 && error?.response?.data?.code === 'ACCOUNT_BANNED') {
      localStorage.removeItem('token');
    }
    if (isTimeoutError(error)) {
      error.userMessage = TIMEOUT_USER_MESSAGE;
      error.message = TIMEOUT_USER_MESSAGE;
    }
    return Promise.reject(error);
  }
);

export default api;

const RESOLVE_REQUEST_TIMEOUT_MS = 180000;
const RESOLVE_MAX_ATTEMPTS = 4;

function isRetryableResolveError(err) {
  if (!err) return false;
  if (!err.response) return true;
  const status = err.response.status;
  return status === 502 || status === 503 || status === 504 || status === 408;
}

/** Admin resolve can be slow; retry on proxy/network blips after on-chain resolve succeeds. */
export async function postAdminResolveWithRetry(path, body) {
  let lastError;
  for (let attempt = 0; attempt < RESOLVE_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await api.post(path, body, { timeout: RESOLVE_REQUEST_TIMEOUT_MS });
    } catch (err) {
      lastError = err;
      if (!isRetryableResolveError(err) || attempt === RESOLVE_MAX_ATTEMPTS - 1) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000 * (attempt + 1)));
    }
  }
  throw lastError;
}
